import { Box } from '@chakra-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef } from 'react'
import { DataCompletenessItem } from './DataCompletenessItem'
import type { DataCompletenessScope } from './dataCompleteness.mapper'
import { type IDataCompletenessItem, completenessIndicatorLayout, itemsScrollContainerStyle } from './dataCompletenessIndicator.utils'

interface VirtualizedDataCompletenessItemsListProps {
  items: IDataCompletenessItem[]
  scope: DataCompletenessScope
}

// Intrinsic item height for a single dot row. The row gap is added inside `estimateSize` because
// `measureElement` reads `getBoundingClientRect().height`, which includes the wrapper's `pb` —
// without this, total height undershoots by ~4px per row and the layout jitters on first paint.
// Real heights are remeasured via `measureElement` once items mount.
const ITEM_ESTIMATED_HEIGHT_PX = 36

export const VirtualizedDataCompletenessItemsList = ({ items, scope }: VirtualizedDataCompletenessItemsListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: index => ITEM_ESTIMATED_HEIGHT_PX + (index < items.length - 1 ? completenessIndicatorLayout.itemRowGapPx : 0),
    overscan: 8,
    getItemKey: index => items[index].id,
  })

  return (
    <Box ref={scrollRef} {...itemsScrollContainerStyle}>
      <Box position={'relative'} width={'100%'} height={`${rowVirtualizer.getTotalSize()}px`}>
        {rowVirtualizer.getVirtualItems().map(virtualItem => {
          const item = items[virtualItem.index]
          const isLastItem = virtualItem.index === items.length - 1
          return (
            <Box
              key={item.id}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
              position={'absolute'}
              top={0}
              left={0}
              right={0}
              transform={`translateY(${virtualItem.start}px)`}
              pb={isLastItem ? 0 : `${completenessIndicatorLayout.itemRowGapPx}px`}
            >
              <DataCompletenessItem item={item} scope={scope} />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
